import React, { useState, useEffect, useCallback } from 'react';
import { clustersAPI, groupsAPI } from '../services/api';

// The two places a Group can be granted, and the API calls for each. A namespace belongs to
// exactly one Project, so giving the SAME namespace to several teams is done with namespace
// grants — they sit beside the Project instead of replacing it.
const TARGETS = {
  project: {
    noun: 'project',
    list:   (cid, id)       => clustersAPI.listProjectGroupBindings(cid, id),
    add:    (cid, id, body) => clustersAPI.addProjectGroupBinding(cid, id, body),
    sync:   (cid, gid)      => clustersAPI.syncProjectGroupBinding(cid, gid),
    remove: (cid, gid)      => clustersAPI.removeProjectGroupBinding(cid, gid),
  },
  namespace: {
    noun: 'namespace',
    list:   (cid, id)       => clustersAPI.listNamespaceGroupBindings(cid, id),
    add:    (cid, id, body) => clustersAPI.addNamespaceGroupBindings(cid, id, body),
    sync:   (cid, gid)      => clustersAPI.syncNamespaceGroupBinding(cid, gid),
    remove: (cid, gid)      => clustersAPI.removeNamespaceGroupBinding(cid, gid),
  },
};

// One readable line for a multi-group grant: failures name the group/member and why.
function summarize(rows) {
  const groupErrors = rows.filter(r => r.error);
  const memberFails = rows.flatMap(r => (r.result?.failed || []).map(f => ({ ...f, group: r.group_name })));
  const ok = rows.filter(r => !r.error).map(r => r.group_name);
  if (!groupErrors.length && !memberFails.length) {
    return { kind: 'ok', text: `Access granted to ${ok.join(', ')}.` };
  }
  const parts = [];
  if (ok.length) parts.push(`Granted to ${ok.join(', ')}`);
  groupErrors.forEach(r => parts.push(`${r.group_name}: ${r.error}`));
  if (memberFails.length) {
    parts.push(`${memberFails.length} member(s) could not be set up `
      + `(${memberFails.map(f => f.username || `user ${f.user_id}`).join(', ')}): ${memberFails[0].error}. Use Sync to retry`);
  }
  return { kind: 'warn', text: parts.join('. ') + '.' };
}

/**
 * Groups with access to one project or one namespace, and a multi-select to grant more.
 *
 * Granting a group binds every current member; membership changes on the Groups page
 * re-sync it, so a new team member gets access without anyone visiting this screen.
 * `granted_count < member_count` means some members failed (usually Rancher was
 * unreachable) — Sync retries them.
 *
 * Styles come in as `S` from the parent page so it matches the panel around it.
 */
export default function GroupAccessPanel({ clusterId, kind, targetId, S, setMsg, onChanged }) {
  const api = TARGETS[kind];
  const [grants, setGrants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!clusterId || !targetId) return;
    try {
      const r = await api.list(clusterId, targetId);
      setGrants(r.data.group_bindings || []);
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Failed to load group access.' });
    }
  }, [api, clusterId, targetId, setMsg]);

  useEffect(() => { load(); setPicked(new Set()); }, [load]);

  useEffect(() => {
    groupsAPI.list().then(r => setGroups(r.data || [])).catch(() => setGroups([]));
  }, []);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
      onChanged && onChanged();
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Request failed.' });
    } finally { setBusy(false); }
  };

  const togglePick = (id) => setPicked(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const doGrant = () => picked.size && run(async () => {
    const r = await api.add(clusterId, targetId, { group_ids: [...picked], role: 'project-member' });
    setMsg(summarize(r.data.group_bindings || []));
    setPicked(new Set());
  });

  const doSync = (g) => run(async () => {
    const r = await api.sync(clusterId, g.id);
    setMsg(summarize([{ ...r.data, group_name: g.group_name }]));
  });

  const doRevoke = (g) => {
    if (!window.confirm(`Revoke ${g.group_name}'s access to this ${api.noun}? `
      + 'Members keep any access granted to them individually.')) return;
    run(async () => {
      await api.remove(clusterId, g.id);
      setMsg({ kind: 'ok', text: `${g.group_name} no longer has access.` });
    });
  };

  const granted = new Set(grants.map(g => g.group_id));
  const available = groups.filter(g => !granted.has(g.id));

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>GROUPS</div>
      {grants.length > 0 && (
        <table style={{ ...S.table, marginBottom: 10 }}>
          <thead>
            <tr>
              <th style={S.th}>Group</th>
              <th style={S.th}>Members with access</th>
              <th style={S.th}>Role</th>
              <th style={S.th} />
            </tr>
          </thead>
          <tbody>
            {grants.map(g => {
              const partial = g.granted_count < g.member_count;
              return (
                <tr key={g.id}>
                  <td style={S.td}>{g.group_name}</td>
                  <td style={{ ...S.td, color: partial ? 'var(--color-warning-500)' : undefined }}>
                    {g.granted_count} / {g.member_count}
                  </td>
                  <td style={S.td}>{g.role}</td>
                  <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button style={{ ...S.btn, padding: '4px 10px', fontSize: 12, marginRight: 6 }}
                      onClick={() => doSync(g)} disabled={busy}
                      title="Re-apply: retry failed members and repair changes made outside SarvKube">
                      Sync
                    </button>
                    <button style={S.btnDanger} onClick={() => doRevoke(g)} disabled={busy}>Revoke</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {available.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--muted-2)' }}>
          {groups.length ? 'Every group already has access.' : 'No groups yet — create them on the Groups page.'}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {available.map(g => (
              <label key={g.id} style={{
                ...S.btn, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6,
                ...(picked.has(g.id) ? { borderColor: 'var(--brand)', color: 'var(--brand)' } : {}),
              }}>
                <input type="checkbox" checked={picked.has(g.id)} onChange={() => togglePick(g.id)} disabled={busy} />
                {g.name}
                <span style={{ color: 'var(--muted)', fontSize: 11 }}>{(g.member_ids || []).length}</span>
              </label>
            ))}
          </div>
          <div style={S.toolbar}>
            <button style={S.btnPrimary} onClick={doGrant} disabled={busy || picked.size === 0}>
              {busy ? 'Granting…' : `Grant ${picked.size || ''} group${picked.size === 1 ? '' : 's'}`.replace('  ', ' ')}
            </button>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              Pick one or more. Everyone in each group gets access; people added later get it automatically.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
